import React, { useContext, useState } from 'react';
import { Card } from '@rewind-ui/core';
import { TbCircleArrowRightFilled } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { HiInformationCircle, HiEye, HiEyeOff } from 'react-icons/hi';
import { TextInput, Alert, Button, Spinner } from 'flowbite-react';
import { AppContext } from '../../../modules/contexts/AppContexts';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FaXmark } from 'react-icons/fa6';
import { FcGoogle } from 'react-icons/fc';

// Validation schema
const validationSchema = Yup.object().shape({
    user_name: Yup.string().required('Please enter your username.'),
    user_password: Yup.string()
        .required('Password is required.')
        .min(8, 'Password must be at least 8 characters long.')
});

const LoginCard = ({ switchToSignUp, switchToResetPassword }) => {
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState();
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordReset, setShowPasswordReset] = useState(false);
    const { userLogin, loading, setStartAuth } = useContext(AppContext);

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
    } = useForm({
        resolver: yupResolver(validationSchema)
    });
    
    const closeMethod = () => {
        setStartAuth(false);
    };

    const passwordValue = useWatch({
        control,
        name: 'user_password',
        defaultValue: ''
    });

    const onSubmit = async (data) => {
        const userData = {
            username: data.user_name,
            password: data.user_password,
        };
        try {
            setErrorMessage(null);
            setShowPasswordReset(false);
            await userLogin(userData);
            closeMethod();
            window.location.href = '/vetting';
        } catch (error) {
            setErrorMessage(error.message);
            
            // Show password reset option for authentication errors
            if (error.isAuthError) {
                setShowPasswordReset(true);
            }
        }
    };

    const handleGoogleSignIn = () => {
        const clientId = "179883348163-30kg564rj670heq9n2ol2remfj7b24qn.apps.googleusercontent.com";
        const redirectUri = "https://unido-api.sustainable.co.ke/oauth2redirect";
        const scope = "https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email";
        const responseType = "code";
        const accessType = "offline";

        const state = btoa(JSON.stringify({
            origin_url: window.location.origin
        }));

        const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&state=${state}`;
        window.location.href = oauthUrl;
    };

    const handlePasswordReset = () => {
        setShowPasswordReset(false);
        switchToResetPassword();
    };

    return (
        <Card size='lg' className='flex flex-col border bg-white rounded-md shadow-sm divide-y divide-gray-100 border-gray-100 relative '>
            <div className='flex justify-end absolute right-0 top-0 p-6'>
                <Button onClick={closeMethod} color='red' pill className='pt-1 m-0'>
                    <div className="flex items-center gap-2">
                        <FaXmark />
                        CLOSE
                    </div>
                </Button>
            </div>
            <Card.Body className='flex justify-center '>
                <div className='h-fit flex flex-col gap-5 tablet:gap-10 items-center tablet:items-center justify-center w-11/12 tablet:w-3/4'>
                    <div className='w-full h-fit flex flex-col gap-5 tablet:gap-10 items-center tablet:items-center justify-center'>
                        <h3 className='text-2xl font-semibold text-center w-full'>Log In</h3>
                        <button 
                            onClick={handleGoogleSignIn}
                            className='flex shadow-lg flex-row bg-white py-3 gap-4 rounded-lg w-full justify-center items-center hover:bg-gray-50 transition-colors'
                        >
                            <FcGoogle className='text-4xl' />
                            Sign In With Google
                        </button>
                        <span className='gap-5 w-full text-center justify-center flex'>- <b>OR</b> -</span>
                        <form onSubmit={handleSubmit(onSubmit)} className='w-full flex flex-col gap-7'>
                            {errorMessage && (
                                <div className="flex flex-col gap-3">
                                    <Alert color='failure' icon={HiInformationCircle}>
                                        <div className="flex flex-col gap-2">
                                            <p className="font-medium">{errorMessage}</p>
                                            {showPasswordReset && (
                                                <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-red-200">
                                                    <p className="text-sm">Can't remember your password?</p>
                                                    <button
                                                        type="button"
                                                        onClick={handlePasswordReset}
                                                        className="inline-flex items-center px-3 py-1 text-sm font-medium text-red-800 bg-red-100 rounded-md hover:bg-red-200 transition-colors w-fit"
                                                    >
                                                        Reset Password
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </Alert>
                                </div>
                            )}

                            <div className='w-full'>
                                <TextInput
                                    {...register('user_name')}
                                    type='text'
                                    placeholder='Username *'
                                    helperText={errors.user_name?.message}
                                    color={errors.user_name ? 'failure' : 'gray'}
                                />
                            </div>

                            <div className='w-full relative'>
                                <TextInput
                                    {...register('user_password')}
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder='Password *'
                                    helperText={errors.user_password?.message}
                                    color={errors.user_password ? 'failure' : 'gray'}
                                />
                                <button
                                    type='button'
                                    onClick={() => setShowPassword(!showPassword)}
                                    className={`absolute inset-y-0 right-0 flex items-center px-2 ${errors.user_password ? 'mb-6' : ''} `}
                                >
                                    {showPassword ? <HiEyeOff className='text-gray-500' /> : <HiEye className='text-gray-500' />}
                                </button>
                            </div>

                            <div className='flex flex-col items-center tablet:justify-center justify-center w-full gap-2'>
                                <div className="flex text-xs py-3 tablet:py-0 tablet:text-base gap-0 items-center">
                                    Don't have an account?
                                    <span onClick={switchToSignUp} className="text-blue-600 ms-2 cursor-pointer">Create an account.</span>
                                </div>
                                <Button onClick={handleSubmit} type='submit' pill color='success' disabled={loading} className='pe-0'>
                                    Log In
                                    {loading ? <Spinner className='ms-3' /> : <TbCircleArrowRightFilled className='text-xl ms-3' />}
                                </Button>
                                <div className="flex flex-row w-full justify-between">
                                    <a href='mailto:hello@sustainable.co.ke' className='text-blue-600 ms-2 cursor-pointer w-full text-start text-sm'>
                                        Having trouble signing in?
                                    </a>
                                    <span onClick={handlePasswordReset} className='text-blue-600 ms-2 cursor-pointer w-full text-end text-sm'>
                                        Forgot password?
                                    </span>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

export default LoginCard;